// Support messaging hook — one private conversation per user, plus the
// agent-side inbox. Deliberately online-only (no IndexedDB/outbox layer):
// talking to support requires connectivity anyway, and stale cached replies
// would be worse than a visible error state.

import { useCallback, useRef, useState } from 'react';
import { fetchWithRetry } from '../utils/fetchWithRetry';
import { logger } from '../utils/logger';

const log = logger.child('useSupport');

// Background polls never retry (the interval is the retry) so a flaky network
// can't stack overlapping requests on top of the next tick.
const POLL_RETRY = { retries: 0 };

function authHeaders() {
  return {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${localStorage.getItem('token')}`,
  };
}

async function parseOrThrow(res, fallbackMessage) {
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || fallbackMessage);
  return data;
}

export const useSupport = (apiUrl) => {
  const [conversation, setConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchConversation = useCallback(async () => {
    try {
      setError('');
      const res = await fetchWithRetry(`${apiUrl}/api/support/conversation`, { headers: authHeaders() });
      const data = await parseOrThrow(res, 'Failed to load your conversation');
      setConversation(data.conversation);
      setMessages(data.messages);
    } catch (e) {
      log.error('fetchConversation failed', e);
      setError(e.message);
    } finally {
      setIsLoading(false);
    }
  }, [apiUrl]);

  // Poll variant of fetchConversation: silent on failure (the banner never
  // flashes over a working thread; the next tick retries) and guarded against
  // overlapping in-flight requests.
  const refreshInFlightRef = useRef(false);
  const refreshConversation = useCallback(async () => {
    if (refreshInFlightRef.current) return;
    refreshInFlightRef.current = true;
    try {
      const res = await fetchWithRetry(
        `${apiUrl}/api/support/conversation`,
        { headers: authHeaders() },
        POLL_RETRY
      );
      const data = await parseOrThrow(res, 'Failed to load your conversation');
      setConversation(data.conversation);
      setMessages(data.messages);
    } catch (e) {
      // debug, not warn: a spotty connection fails a poll every tick, and warn
      // records batch-upload to the backend sink.
      log.debug('refreshConversation failed', { message: e.message });
    } finally {
      refreshInFlightRef.current = false;
    }
  }, [apiUrl]);

  const sendMessage = useCallback(async (body) => {
    const res = await fetchWithRetry(`${apiUrl}/api/support/conversation/messages`, {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify({ body }),
    });
    await parseOrThrow(res, 'Failed to send message');
    await fetchConversation();
  }, [apiUrl, fetchConversation]);

  const resolve = useCallback(async () => {
    const res = await fetchWithRetry(`${apiUrl}/api/support/conversation/resolve`, {
      method: 'POST',
      headers: authHeaders(),
    });
    await parseOrThrow(res, 'Failed to resolve conversation');
    await fetchConversation();
  }, [apiUrl, fetchConversation]);

  return { conversation, messages, isLoading, error, fetchConversation, refreshConversation, sendMessage, resolve };
};

export const useAgentSupport = (apiUrl) => {
  const [conversations, setConversations] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchInbox = useCallback(async (status = 'open') => {
    try {
      setError('');
      const res = await fetchWithRetry(
        `${apiUrl}/api/support/agent/conversations?status=${encodeURIComponent(status)}`,
        { headers: authHeaders() }
      );
      const data = await parseOrThrow(res, 'Failed to load inbox');
      setConversations(data.conversations);
    } catch (e) {
      log.error('fetchInbox failed', e);
      setError(e.message);
    } finally {
      setIsLoading(false);
    }
  }, [apiUrl]);

  // Cheap change cursor for the inbox poll; also the agent presence heartbeat
  // the backend uses for "email me when away". Returns { last_message_id,
  // open_count } — refetch the expensive inbox/thread only when it changes.
  const fetchUpdates = useCallback(async () => {
    const res = await fetchWithRetry(
      `${apiUrl}/api/support/agent/updates`,
      { headers: authHeaders() },
      POLL_RETRY
    );
    return parseOrThrow(res, 'Failed to poll for updates');
  }, [apiUrl]);

  // Poll variant of fetchInbox: no retries, no error banner, keeps the current
  // list when a background tick fails.
  const refreshInbox = useCallback(async (status = 'open') => {
    const res = await fetchWithRetry(
      `${apiUrl}/api/support/agent/conversations?status=${encodeURIComponent(status)}`,
      { headers: authHeaders() },
      POLL_RETRY
    );
    const data = await parseOrThrow(res, 'Failed to load inbox');
    setConversations(data.conversations);
  }, [apiUrl]);

  const fetchAgentConversation = useCallback(async (id) => {
    const res = await fetchWithRetry(`${apiUrl}/api/support/agent/conversations/${id}`, {
      headers: authHeaders(),
    });
    return parseOrThrow(res, 'Failed to load conversation');
  }, [apiUrl]);

  const agentReply = useCallback(async (id, body) => {
    const res = await fetchWithRetry(`${apiUrl}/api/support/agent/conversations/${id}/messages`, {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify({ body }),
    });
    return parseOrThrow(res, 'Failed to send reply');
  }, [apiUrl]);

  const agentResolve = useCallback(async (id) => {
    const res = await fetchWithRetry(`${apiUrl}/api/support/agent/conversations/${id}/resolve`, {
      method: 'POST',
      headers: authHeaders(),
    });
    return parseOrThrow(res, 'Failed to resolve conversation');
  }, [apiUrl]);

  const fetchNotificationSettings = useCallback(async () => {
    const res = await fetchWithRetry(`${apiUrl}/api/support/agent/notifications`, {
      headers: authHeaders(),
    });
    return parseOrThrow(res, 'Failed to load notification settings');
  }, [apiUrl]);

  const updateNotificationSettings = useCallback(async (emailOnNewRequest) => {
    const res = await fetchWithRetry(`${apiUrl}/api/support/agent/notifications`, {
      method: 'PUT',
      headers: authHeaders(),
      body: JSON.stringify({ email_on_new_request: emailOnNewRequest }),
    });
    return parseOrThrow(res, 'Failed to update notification settings');
  }, [apiUrl]);

  return {
    conversations, isLoading, error, fetchInbox, refreshInbox, fetchUpdates,
    fetchAgentConversation, agentReply, agentResolve,
    fetchNotificationSettings, updateNotificationSettings,
  };
};
